import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { FaTrashAlt, FaPlusCircle } from "react-icons/fa";
import "./BankingPage.css";

export default function BankingPage() {
  const { id: businessId } = useParams();

  const [items, setItems] = useState([]);        // banks (plaid_items)
  const [accounts, setAccounts] = useState([]);  // all user accounts (flat)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // link/unlink selections
  const [selectedToUnlink, setSelectedToUnlink] = useState([]);

  // Plaid Link state
  const [linking, setLinking] = useState(false);

  const [selectedLinkId, setSelectedLinkId] = useState("");


  // Cash account popup
  const [showAddCashPopup, setShowAddCashPopup] = useState(false);
  const [cashAccountName, setCashAccountName] = useState("Cash");
  const [cashSubtype, setCashSubtype] = useState("cash");
  const [creatingCash, setCreatingCash] = useState(false);
  const [reconcilingItemId, setReconcilingItemId] = useState(null);

  const load = useCallback(() => {
    if (!businessId) return;
    setLoading(true);
    Promise.all([
      api.get("/plaid/items-by-user"),
      api.get("/plaid/accounts-by-user-flat"),
    ])
      .then(([itemsRes, acctsRes]) => {
        setItems(itemsRes.data || []);
        setAccounts((acctsRes.data || []).filter(a => a.is_active !== 0));
        setErr("");
      })
      .catch((e) => {
        console.error(e);
        setErr("Failed to load banking data.");
      })
      .finally(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  // item_id -> bank_name
  const bankByItem = useMemo(() => {
    const m = new Map();
    for (const it of items) m.set(String(it.item_id), it.bank_name || "");
    return m;
  }, [items]);

  // Accounts linked to THIS business (with bank name on each)
  const linked = useMemo(() => {
    return accounts
      .filter(a => String(a.business_id) === String(businessId))
      .map(a => ({ ...a, bank_name: a.bank_name || bankByItem.get(String(a.item_id)) || "" }));
  }, [accounts, bankByItem, businessId]);

  const linkedSet = useMemo(() => new Set(linked.map(a => String(a.account_id))), [linked]);

  const accountsForItem = (itemId) =>
    accounts.filter((a) => String(a.item_id) === String(itemId) && a.is_active !== 0);

  // —— Actions (using ONLY your endpoints as posted) ——


  
  // Connect bank (no businessId in endpoint)
  const connectBank = () => {
    setLinking(true);
    api.post("/plaid/create_link_token")
      .then(({ data }) => {
        const handler = window.Plaid.create({
          token: data.link_token,
          onSuccess: (public_token) => {
            api.post("/plaid/exchange_public_token", { public_token })
              .then(() => { load(); alert("Bank connected."); })
              .catch((e) => { console.error(e); alert("Linked but failed to refresh list."); });
          },
        });
        handler.open();
      })
      .catch((e) => {
        console.error(e);
        alert("Could not start bank linking.");
      })
      .finally(() => setLinking(false));
  };

  // Re-auth / update mode (no businessId in either endpoint)
  const relinkItem = (item_id) => {
    api.post("/plaid/create_link_token/update", { item_id })
      .then(({ data }) => {
        const handler = window.Plaid.create({
          token: data.link_token,
          onSuccess: () => {
            api.post("/plaid/items/clear-needs-update", { item_id })
              .then(() => { load(); alert("Connection updated."); })
              .catch((e) => { console.error(e); alert("Updated but failed to refresh state."); });
          },
        });
        handler.open();
      })
      .catch((e) => {
        console.error(e);
        alert("Could not start update mode.");
      });
  };

  const reconcileAccountIds = async (item_id) => {
  if (!item_id) return;

  if (
    !window.confirm(
      "Check this bank for changed Plaid account IDs?"
    )
  ) {
    return;
  }

  setReconcilingItemId(item_id);

  try {
    const { data } = await api.post(
      "/plaid/reconcile-account-ids",
      { item_id }
    );

    console.log("Account ID reconciliation:", data);

    const changed = data?.changes?.length || 0;

    if (changed > 0) {
      alert(
        `${changed} account ID${changed === 1 ? "" : "s"} updated successfully.`
      );
    } else {
      alert("No account ID changes found.");
    }

    load();
  } catch (err) {
    console.error("Account reconciliation failed:", err);

    alert(
      err?.response?.data?.error ||
        "Failed to check account IDs."
    );
  } finally {
    setReconcilingItemId(null);
  }
};

  const disableItem = (item_id) => {
    if (!window.confirm("Disable this bank connection?")) return;
    api.post("/plaid/disable_item", { item_id })
      .then(() => load())
      .catch((e) => { console.error(e); alert("Failed to disable bank."); });
  };

  // Link/Unlink (business-scoped)
  const handleLinkSelected = () => {
  if (!selectedLinkId) return alert("Pick an account to link.");
  api.post(`/business/${businessId}/link-accounts`, { account_ids: [selectedLinkId] })
    .then(() => { setSelectedLinkId(""); load(); alert("Linked."); })
    .catch((e) => { console.error(e); alert("Failed to link."); });
};


  const handleUnlinkSelected = () => {
    if (!selectedToUnlink.length) return alert("Select at least one account to unlink.");
    api.delete(`/business/${businessId}/link-accounts`, { data: { account_ids: selectedToUnlink } })
      .then(() => { setSelectedToUnlink([]); load(); alert("Unlinked."); })
      .catch((e) => { console.error(e); alert("Failed to unlink."); });
  };

  // Cash account
  const handleCreateCashAccount = () => {
    if (!cashAccountName.trim()) return alert("Account name is required");
    setCreatingCash(true);
    api.post("/external-accounts", {
      businessId,
      name: cashAccountName.trim(),
      subtype: cashSubtype || "cash",
    })
      .then(() => {
        setShowAddCashPopup(false);
        setCashAccountName("Cash");
        setCashSubtype("cash");
        load();
        alert("Cash account added.");
      })
      .catch((err) => {
        console.error("Error creating cash account:", err);
        alert("Failed to add cash account.");
      })
      .finally(() => setCreatingCash(false));
  };

  if (!businessId) {
    return (
      <div className="banking-page">
        <div className="banking-error">
        No Bank Accounts 
        </div>
      </div>
    );
  }
  if (loading) return <div className="banking-page">Loading…</div>;
  if (err) return <div className="banking-page"><div className="banking-error">{err}</div></div>;

  return (
    <div className="banking-page">
      {/* Header actions */}
  
        <h3 className="banking-title">Banking</h3>
        
      

      {/* Linked to this business */}
      <section className="banking-panel">
        <div className="banking-panel-head">
          <h4 className="banking-panel-title">Accounts linked to this business</h4>
          <button
            className="banking-btn"
            onClick={handleUnlinkSelected}
            disabled={selectedToUnlink.length === 0}
          >
            Unlink Selected
          </button>
        </div>

        {linked.length === 0 ? (
          <p className="banking-muted">None linked yet.</p>
        ) : (
          <table className="banking-table">
            <thead>
              <tr>
                <th className="banking-th"></th>
                <th className="banking-th">Bank</th>
                <th className="banking-th">Account</th>
                <th className="banking-th">Subtype</th>
                <th className="banking-th">Mask</th>
              </tr>
            </thead>
            <tbody>
              {linked.map((a) => (
                <tr key={a.account_id}>
                  <td className="banking-td">
                    <input
                      type="checkbox"
                      checked={selectedToUnlink.includes(a.account_id)}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSelectedToUnlink(prev =>
                          on ? [...prev, a.account_id] : prev.filter(id => id !== a.account_id)
                        );
                      }}
                    />
                  </td>
                  <td className="banking-td">{a.bank_name || "Bank"}</td>
                  <td className="banking-td">{a.name || a.subtype || "Account"}</td>
                  <td className="banking-td">{a.subtype || ""}</td>
                  <td className="banking-td">{a.mask ? `••••${a.mask}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Link more (ALL user accounts) */}
      <section className="banking-panel">
  <div className="banking-panel-head">
    <h4 className="banking-panel-title">Link accounts to this business</h4>
    <button
      className="banking-btn"
      onClick={handleLinkSelected}
      disabled={!selectedLinkId}
    >
      Link Selected
    </button>
  </div>

  <select
    className="banking-select"
    value={selectedLinkId}
    onChange={(e) => setSelectedLinkId(e.target.value)}
  >
    <option value="">Select an account…</option>
    {accounts.map((a) => {
      const bank = a.bank_name || bankByItem.get(String(a.item_id)) || "Bank";
      const linkedHere = linkedSet.has(String(a.account_id));
      const label =
        `${bank} — ${a.name || a.subtype || "Account"}${a.mask ? ` •${a.mask}` : ""}` +
        (linkedHere
          ? "  (linked here)"
          : a.business_id && String(a.business_id) !== String(businessId)
          ? "  (linked to another business)"
          : "");
      return (
        <option
          key={a.account_id}
          value={a.account_id}
          disabled={linkedHere} // prevent linking the one already linked here
        >
          {label}
        </option>
      );
    })}
  </select>
</section>

<section>
  <div className="banking-actions">
          <button className="banking-btn banking-btn-primary" onClick={connectBank} disabled={linking}>
            {linking ? "Opening Plaid…" : "Connect Bank"}
          </button>
          <button className="banking-btn banking-btn-secondary" onClick={() => setShowAddCashPopup(true)}>
            <FaPlusCircle className="banking-icon" /> Add Cash Account
          </button>
        </div>
</section>


      {/* Connected Banks (cards) */}
      <section className="banking-panel">
        <h4 className="banking-panel-title">Connected Banks</h4>

        {items.length === 0 ? (
          <div className="banking-empty">No banks connected yet.</div>
        ) : (
          <div className="banking-bank-grid">
            {items.map((it) => {
              const accts = accountsForItem(it.item_id);
              return (
                <div className="banking-bank-card" key={it.item_id}>
                  <div className="banking-bank-card-header">
                    <div className="banking-bank-name">{it.bank_name || "Bank"}</div>
                    {it.needs_update ? (
                      <span className="banking-needs-update">Needs update</span>
                    ) : null}
                  </div>

                  <div className="banking-bank-card-body">
                    {accts.length === 0 ? (
                      <em className="banking-muted">No accounts for this bank.</em>
                    ) : (
                      <ul className="banking-account-list">
                        {accts.map((a) => {
                          const isLinkedHere = linkedSet.has(String(a.account_id));
                          return (
                            <li key={a.account_id} className="banking-account-list-item">
                              <div className="banking-account-name">
                                {a.name || a.subtype || "Account"}
                                {a.mask ? ` •${a.mask}` : ""}
                                {isLinkedHere && (
                                  <span className="banking-linked-pill">Linked to this business</span>
                                )}
                              </div>
                              <div className="banking-account-sub">
                                {(a.type || "").toLowerCase()}
                                {a.subtype ? ` / ${(a.subtype || "").toLowerCase()}` : ""}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <div className="banking-bank-card-footer">
  {it.needs_update ? (
    <button
      className="banking-btn"
      onClick={() => relinkItem(it.item_id)}
    >
      Fix connection
    </button>
  ) : null}

  <button
    className="banking-btn"
    onClick={() => reconcileAccountIds(it.item_id)}
    disabled={reconcilingItemId === it.item_id}
  >
    {reconcilingItemId === it.item_id
      ? "Checking..."
      : "Refresh Accounts"}
  </button>

  <button
    className="banking-btn-danger"
    onClick={() => disableItem(it.item_id)}
    title="Disable Bank"
  >
    <FaTrashAlt />
  </button>
</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Add Cash Account popup */}
      {showAddCashPopup && (
        <>
          <div className="banking-overlay" onClick={() => setShowAddCashPopup(false)} />
          <div className="banking-popup">
            <h4 className="banking-popup-title">Add Cash Account</h4>
            <label className="banking-label">Account Name</label>
            <input
              className="banking-input"
              value={cashAccountName}
              onChange={(e) => setCashAccountName(e.target.value)}
              placeholder="e.g., Cash, Petty Cash, External Checking"
            />
            <label className="banking-label" style={{ marginTop: 10 }}>Subtype</label>
            <select
              className="banking-input"
              value={cashSubtype}
              onChange={(e) => setCashSubtype(e.target.value)}
            >
              <option value="cash">cash</option>
              <option value="external">external</option>
              <option value="savings">savings</option>
              <option value="checking">checking</option>
            </select>
            <div className="banking-popup-actions">
              <button className="banking-btn" disabled={creatingCash} onClick={handleCreateCashAccount}>
                {creatingCash ? "Adding..." : "Add Account"}
              </button>
              <button className="banking-btn" onClick={() => setShowAddCashPopup(false)}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}



// // src/pages/BankingPage.js
// import React, { useEffect, useMemo, useState, useCallback } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";

// const th = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 };
// const td = { padding: "8px 10px", borderBottom: "1px solid #f1f5f9" };

// export default function BankingPage() {
//   const { id: businessId } = useParams();
//   const [items, setItems] = useState([]);       // banks (item_id, bank_name, etc.)
//   const [accounts, setAccounts] = useState([]); // ALL user accounts (flat)
//   const [loading, setLoading] = useState(true);
//   const [err, setErr] = useState("");
//   const [selectedToUnlink, setSelectedToUnlink] = useState([]);
//   const [pendingLinkIds, setPendingLinkIds] = useState([]);

//   const load = useCallback(async () => {
//     if (!businessId) return;
//     setLoading(true);
//     try {
//       const [itemsRes, acctsRes] = await Promise.all([
//         api.get("/plaid/items-by-user"),
//         api.get("/plaid/accounts-by-user-flat"),
//       ]);
//       setItems(itemsRes.data || []);
//       setAccounts((acctsRes.data || []).filter(a => a.is_active !== 0));
//       setErr("");
//     } catch (e) {
//       console.error(e);
//       setErr("Failed to load accounts.");
//     } finally {
//       setLoading(false);
//     }
//   }, [businessId]);

//   useEffect(() => { load(); }, [load]);

//   // Map item_id -> bank_name
//   const bankByItem = useMemo(() => {
//     const m = new Map();
//     for (const it of items) m.set(String(it.item_id), it.bank_name || "");
//     return m;
//   }, [items]);

//   // Linked to THIS business (and add bank name)
//   const linked = useMemo(() => {
//     return accounts
//       .filter(a => String(a.business_id) === String(businessId))
//       .map(a => ({
//         ...a,
//         bank_name: a.bank_name || bankByItem.get(String(a.item_id)) || "",
//       }));
//   }, [accounts, bankByItem, businessId]);

//   // Fast lookup (for tagging in the select)
//   const linkedSet = useMemo(() => new Set(linked.map(a => String(a.account_id))), [linked]);

//   const handleLinkSelected = async () => {
//     if (!pendingLinkIds.length) return alert("Select at least one account to link.");
//     try {
//       await api.post(`/business/${businessId}/link-accounts`, { account_ids: pendingLinkIds });
//       setPendingLinkIds([]);
//       await load();
//       alert("Linked.");
//     } catch (e) {
//       console.error(e);
//       alert("Failed to link.");
//     }
//   };

//   const handleUnlinkSelected = async () => {
//     if (!selectedToUnlink.length) return alert("Select at least one account to unlink.");
//     try {
//       await api.delete(`/business/${businessId}/link-accounts`, { data: { account_ids: selectedToUnlink } });
//       setSelectedToUnlink([]);
//       await load();
//       alert("Unlinked.");
//     } catch (e) {
//       console.error(e);
//       alert("Failed to unlink.");
//     }
//   };

//   if (!businessId) {
//     return (
//       <div style={{ padding: 16, color: "#b91c1c" }}>
//         Missing business id. Route should look like <code>/dashboard/business/:businessId/banking</code>.
//       </div>
//     );
//   }
//   if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
//   if (err) return <div style={{ padding: 16, color: "#b91c1c" }}>{err}</div>;

//   return (
//     <div style={{ padding: 16 }}>
//       <h3 style={{ marginTop: 0 }}>Banking</h3>

//       {/* Linked to this business (with BANK NAME) */}
//       <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, marginBottom: 16 }}>
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//           <h4 style={{ margin: 0 }}>Accounts linked to this business</h4>
//           <button onClick={handleUnlinkSelected} disabled={selectedToUnlink.length === 0}>Unlink Selected</button>
//         </div>

//         {linked.length === 0 ? (
//           <p style={{ margin: 0, color: "#64748b" }}>None linked yet.</p>
//         ) : (
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <thead>
//               <tr>
//                 <th style={th}></th>
//                 <th style={th}>Bank</th>
//                 <th style={th}>Account</th>
//                 <th style={th}>Subtype</th>
//                 <th style={th}>Mask</th>
//               </tr>
//             </thead>
//             <tbody>
//               {linked.map((a) => (
//                 <tr key={a.account_id}>
//                   <td style={td}>
//                     <input
//                       type="checkbox"
//                       checked={selectedToUnlink.includes(a.account_id)}
//                       onChange={(e) => {
//                         const on = e.target.checked;
//                         setSelectedToUnlink(prev =>
//                           on ? [...prev, a.account_id] : prev.filter(id => id !== a.account_id)
//                         );
//                       }}
//                     />
//                   </td>
//                   <td style={td}>{a.bank_name || "Bank"}</td>
//                   <td style={td}>{a.name || a.subtype || "Account"}</td>
//                   <td style={td}>{a.subtype || ""}</td>
//                   <td style={td}>{a.mask ? `••••${a.mask}` : ""}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </section>

//       {/* Link more (ALL user accounts shown; tags those already linked here) */}
//       <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//           <h4 style={{ margin: 0 }}>Link accounts to this business</h4>
//           <button onClick={handleLinkSelected} disabled={pendingLinkIds.length === 0}>Link Selected</button>
//         </div>

//         <select
//           multiple
//           size={Math.min(12, Math.max(6, accounts.length))}
//           style={{ width: "100%", maxWidth: 760 }}
//           value={pendingLinkIds}
//           onChange={(e) => {
//             const values = Array.from(e.target.selectedOptions).map((o) => o.value);
//             setPendingLinkIds(values);
//           }}
//         >
//           {accounts.map((a) => {
//             const bank = a.bank_name || bankByItem.get(String(a.item_id)) || "Bank";
//             const linkedHere = linkedSet.has(String(a.account_id));
//             const label =
//               `${bank} — ${a.name || a.subtype || "Account"}${a.mask ? ` •${a.mask}` : ""}` +
//               (linkedHere
//                 ? "  (linked here)"
//                 : a.business_id && String(a.business_id) !== String(businessId)
//                 ? "  (linked to another business)"
//                 : "");
//             return (
//               <option key={a.account_id} value={a.account_id}>
//                 {label}
//               </option>
//             );
//           })}
//         </select>
//       </section>
//     </div>
//   );
// }




// // src/pages/BankingPage.js
// import React, { useEffect, useState, useCallback, useMemo } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";

// const th = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 };
// const td = { padding: "8px 10px", borderBottom: "1px solid #f1f5f9" };

// export default function BankingPage() {
//   const { id: businessId } = useParams();
//   const [linked, setLinked] = useState([]);        // from /plaid/by-accounts/:businessId
//   const [allAccounts, setAllAccounts] = useState([]); // from /plaid/accounts-by-user-flat
//   const [loading, setLoading] = useState(true);
//   const [err, setErr] = useState("");
//   const [selectedToUnlink, setSelectedToUnlink] = useState([]);
//   const [pendingLinkIds, setPendingLinkIds] = useState([]);

//   const load = useCallback(async () => {
//     if (!businessId) return;
//     setLoading(true);
//     try {
//       const [byBizRes, allRes] = await Promise.all([
//         api.get(`/plaid/by-accounts/${businessId}`),
//         api.get("/plaid/accounts-by-user-flat"),
//       ]);
//       setLinked(byBizRes.data || []);
//       setAllAccounts((allRes.data || []).filter(a => a.is_active !== 0));
//       setErr("");
//     } catch (e) {
//       console.error(e);
//       setErr("Failed to load accounts.");
//     } finally {
//       setLoading(false);
//     }
//   }, [businessId]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   const linkedSet = useMemo(
//     () => new Set((linked || []).map(a => String(a.account_id))),
//     [linked]
//   );

//   const handleLinkSelected = async () => {
//     if (!pendingLinkIds.length) return alert("Select at least one account to link.");
//     try {
//       await api.post(`/business/${businessId}/link-accounts`, { account_ids: pendingLinkIds });
//       setPendingLinkIds([]);
//       await load();
//       alert("Linked.");
//     } catch (e) {
//       console.error(e);
//       alert("Failed to link.");
//     }
//   };

//   const handleUnlinkSelected = async () => {
//     if (!selectedToUnlink.length) return alert("Select at least one account to unlink.");
//     try {
//       await api.delete(`/business/${businessId}/link-accounts`, { data: { account_ids: selectedToUnlink } });
//       setSelectedToUnlink([]);
//       await load();
//       alert("Unlinked.");
//     } catch (e) {
//       console.error(e);
//       alert("Failed to unlink.");
//     }
//   };

//   if (!businessId) {
//     return (
//       <div style={{ padding: 16, color: "#b91c1c" }}>
//         Missing business id. Expected route like <code>/dashboard/business/:businessId/banking</code>.
//       </div>
//     );
//   }

//   if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
//   if (err) return <div style={{ padding: 16, color: "#b91c1c" }}>{err}</div>;

//   return (
//     <div style={{ padding: 16 }}>
//       <h3 style={{ marginTop: 0 }}>Banking</h3>

//       {/* Linked to this business */}
//       <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, marginBottom: 16 }}>
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//           <h4 style={{ margin: 0 }}>Accounts linked to this business</h4>
//           <button onClick={handleUnlinkSelected} disabled={selectedToUnlink.length === 0}>Unlink Selected</button>
//         </div>

//         {linked.length === 0 ? (
//           <p style={{ margin: 0, color: "#64748b" }}>None linked yet.</p>
//         ) : (
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <thead>
//               <tr>
//                 <th style={th}></th>
//                 <th style={th}>Account</th>
//                 <th style={th}>Subtype</th>
//                 <th style={th}>Mask</th>
//               </tr>
//             </thead>
//             <tbody>
//               {linked.map((a) => (
//                 <tr key={a.account_id}>
//                   <td style={td}>
//                     <input
//                       type="checkbox"
//                       checked={selectedToUnlink.includes(a.account_id)}
//                       onChange={(e) => {
//                         const on = e.target.checked;
//                         setSelectedToUnlink((prev) =>
//                           on ? [...prev, a.account_id] : prev.filter(id => id !== a.account_id)
//                         );
//                       }}
//                     />
//                   </td>
//                   <td style={td}>
//                     {a.name || a.subtype || "Account"}
//                   </td>
//                   <td style={td}>{a.subtype || ""}</td>
//                   <td style={td}>{a.mask ? `••••${a.mask}` : ""}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </section>

//       {/* Link more (ALL user accounts shown) */}
//       <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//           <h4 style={{ margin: 0 }}>Link accounts to this business</h4>
//           <button onClick={handleLinkSelected} disabled={pendingLinkIds.length === 0}>Link Selected</button>
//         </div>

//         <div style={{ color: "#64748b", fontSize: 13, marginBottom: 6 }}>
//           The list below shows <strong>all</strong> your bank accounts. Items marked “(linked here)” are already linked to this business.
//         </div>

//         <select
//           multiple
//           size={Math.min(12, Math.max(6, allAccounts.length))}
//           style={{ width: "100%", maxWidth: 760 }}
//           value={pendingLinkIds}
//           onChange={(e) => {
//             const values = Array.from(e.target.selectedOptions).map((o) => o.value);
//             setPendingLinkIds(values);
//           }}
//         >
//           {allAccounts.map((a) => {
//             const linkedHere = linkedSet.has(String(a.account_id));
//             const label = `${a.bank_name || "Bank"} — ${a.name || a.subtype || "Account"}${a.mask ? ` •${a.mask}` : ""}${linkedHere ? "  (linked here)" : ""}`;
//             return (
//               <option key={a.account_id} value={a.account_id}>
//                 {label}
//               </option>
//             );
//           })}
//         </select>
//       </section>
//     </div>
//   );
// }


// // src/pages/BankingPage.js
// import React, { useEffect, useState, useCallback } from "react";
// import api from "../services/api";
// import { FaTrashAlt } from "react-icons/fa";

// const BankingPage = () => {
//   const [items, setItems] = useState([]);            // banks (Plaid items)
//   const [accounts, setAccounts] = useState([]);      // all user accounts (flat)
//   const [loading, setLoading] = useState(true);
//   const [linking, setLinking] = useState(false);

//   const load = useCallback(async () => {
//     setLoading(true);
//     try {
//       const [itemsRes, acctsRes] = await Promise.all([
//         api.get("/plaid/items-by-user"),
//         api.get("/plaid/accounts-by-user-flat"), // swap to /plaid/accounts-by-user and flatten if needed
//       ]);
//       setItems(itemsRes.data || []);
//       setAccounts(acctsRes.data || []);
//     } catch (e) {
//       console.error(e);
//       alert("Failed to load your banking data.");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     load();
//   }, [load]);

//   const openPlaidToLinkAnotherBank = async () => {
//     try {
//       setLinking(true);
//       // USER-SCOPED link token (no businessId)
//       const { data } = await api.post("/plaid/link_token/item");
//       const handler = window.Plaid.create({
//         token: data.link_token,
//         onSuccess: async (public_token) => {
//           try {
//             // USER-SCOPED exchange (no businessId)
//             await api.post("/plaid/exchange_public_token", { public_token });
//             await load();
//             alert("Bank linked.");
//           } catch (err) {
//             console.error(err);
//             alert("Linked but failed to refresh list.");
//           }
//         },
//       });
//       handler.open();
//     } catch (e) {
//       console.error(e);
//       alert("Could not start bank linking.");
//     } finally {
//       setLinking(false);
//     }
//   };

//   const relinkItem = async (item_id) => {
//     try {
//       const { data } = await api.post("/plaid/link_token/update", { item_id });
//       const handler = window.Plaid.create({
//         token: data.link_token,
//         onSuccess: async () => {
//           try {
//             await api.post("/plaid/items/clear-needs-update", { item_id });
//             await load();
//             alert("Connection updated.");
//           } catch (err) {
//             console.error(err);
//             alert("Updated but failed to refresh state.");
//           }
//         },
//       });
//       handler.open();
//     } catch (e) {
//       console.error(e);
//       alert("Could not start update mode.");
//     }
//   };

//   const disableItem = async (item_id) => {
//     if (!window.confirm("Disable this bank connection?")) return;
//     try {
//       await api.post("/plaid/disable_item", { item_id });
//       await load();
//     } catch (e) {
//       console.error(e);
//       alert("Failed to disable.");
//     }
//   };

//   const accountsForItem = (itemId) =>
//     accounts.filter((a) => a.item_id === itemId && a.is_active !== 0);

//   if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

//   return (
//     <div className="banking-page" style={{ padding: 16 }}>
//       <h3>Banking</h3>

//       <div style={{ display: "flex", gap: 12, margin: "12px 0" }}>
//         <button onClick={openPlaidToLinkAnotherBank} disabled={linking}>
//           {linking ? "Opening Plaid…" : "Link another bank"}
//         </button>
//       </div>

//       <section
//         style={{
//           border: "1px solid #ddd",
//           borderRadius: 8,
//           padding: 12,
//           marginBottom: 16,
//         }}
//       >
//         <h4>Your Connected Banks</h4>

//         {items.length === 0 ? (
//           <p>No banks connected yet.</p>
//         ) : (
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <thead>
//               <tr>
//                 <th style={{ textAlign: "left", padding: 8 }}>Bank</th>
//                 <th style={{ textAlign: "left", padding: 8 }}>Accounts</th>
//                 <th style={{ textAlign: "left", padding: 8 }}>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {items.map((it) => {
//                 const accts = accountsForItem(it.item_id);
//                 return (
//                   <tr key={it.item_id}>
//                     <td style={{ padding: 8, verticalAlign: "top" }}>
//                       {it.bank_name || "Bank"}
//                       {it.needs_update ? (
//                         <span
//                           style={{
//                             marginLeft: 8,
//                             color: "#c00",
//                             fontWeight: 600,
//                           }}
//                         >
//                           Needs update
//                         </span>
//                       ) : null}
//                     </td>

//                     <td style={{ padding: 8 }}>
//                       {accts.length === 0 ? (
//                         <em>No accounts</em>
//                       ) : (
//                         <div
//                           style={{
//                             display: "flex",
//                             flexWrap: "wrap",
//                             gap: 8,
//                           }}
//                         >
//                           {accts.map((a) => (
//                             <div
//                               key={a.account_id}
//                               style={{
//                                 border: "1px solid #eee",
//                                 borderRadius: 8,
//                                 padding: "6px 10px",
//                               }}
//                               title={`${a.type || ""}/${a.subtype || ""}`}
//                             >
//                               {(a.name || a.subtype || "Account") +
//                                 (a.mask ? ` •${a.mask}` : "")}
//                             </div>
//                           ))}
//                         </div>
//                       )}
//                     </td>

//                     <td style={{ padding: 8, whiteSpace: "nowrap" }}>
//                       {it.needs_update ? (
//                         <button onClick={() => relinkItem(it.item_id)}>
//                           Fix connection
//                         </button>
//                       ) : null}
//                       <FaTrashAlt
//                         onClick={() => disableItem(it.item_id)}
//                         style={{
//                           cursor: "pointer",
//                           color: "red",
//                           marginLeft: 8,
//                           verticalAlign: "middle",
//                         }}
//                         title="Disable Bank"
//                       />
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}

//         <p style={{ marginTop: 8, fontStyle: "italic" }}>
//           Tip: You can connect multiple banks. Accounts shown are tied to your
//           user; businesses will see transactions if those accounts are linked
//           in your workflow.
//         </p>
//       </section>
//     </div>
//   );
// };

// export default BankingPage;
